import type { Instruction } from '../TeoApi';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

export type StyleHandlerEvents = {
  instructionChanged: (detail: Instruction | undefined) => void;
  categoryChanged: (detail: StyleCategory | undefined) => void;
  numbersChanged: (detail: StyleValues) => void;
  loading: (detail: boolean) => void;
};

export const StyleCategories = ['causal', 'passive', 'dialog'] as const;
export type StyleCategory = (typeof StyleCategories)[number];

type StyleValues = Record<
  StyleCategory,
  {
    text_propertis: {
      name: string;
      label: string;
      value: number;
    }[];
    label: string;
  }
>;

export class StyleHandler {
  public events = new TeoEventTarget<StyleHandlerEvents>();
  private _currentCategory?: StyleCategory;
  private _currentInstruction?: Instruction;
  private _styleValues: StyleValues;
  private propertyMarkers: Record<string, string[]> = {
    causal: ['causal_connector'],
    passive: ['passive_sentence'],
    dialog: ['dialog_word', 'dialog_phrase_part'],
    dialog_particles: ['dialog_word'],
    dialog_phrases: ['dialog_phrase_part'],
  };

  constructor(private text: TeoText) {
    this._styleValues = this.getInitialStyleValues();
    this.text.events.addEventListener('gotAnalysis', () => {
      this._styleValues = this.getInitialStyleValues();
    });
    this.text.ui.events.addEventListener('styleCategory', (category) => {
      this.currentCategory = category as StyleCategory | undefined;
    });
    this.text.ui.events.addEventListener('styleTextProperty', (textProperty) => {
      if (textProperty) {
        this.text.markers.setMarkers(this.getTextPropertyMarkers(textProperty));
      } else {
        this.text.markers.setMarkers([]);
      }
    });
  }

  get currentCategory(): StyleCategory | undefined {
    return this._currentCategory;
  }

  set currentCategory(category: StyleCategory | undefined) {
    if (this._currentCategory !== category) {
      this._currentCategory = category;
      this.updateInstruction();
      this.events.triggerEvent('categoryChanged', category);
    }
  }

  get currentInstruction(): Instruction | undefined {
    return this._currentInstruction;
  }

  get styleValues(): StyleValues {
    return this._styleValues;
  }

  updateInstruction(): void {
    const newInstruction = this.getInstruction();
    if (newInstruction !== this._currentInstruction) {
      this._currentInstruction = newInstruction;
      this.events.triggerEvent('instructionChanged', this._currentInstruction);
    }
  }

  private getTextPropertyMarkers(property: string): string[] {
    return this.propertyMarkers[property] || [];
  }

  private getInstruction(): Instruction | undefined {
    if (!this._currentCategory) {
      return;
    }
    const instructions = this.text.analysis?.data.additional_instructions;
    if (!instructions) {
      return;
    }
    return instructions.find(
      (instruction) => instruction.text_property_name === this._currentCategory,
    );
  }

  private getInitialStyleValues(): StyleValues {
    return {
      causal: {
        text_propertis: [
          {
            name: 'causal',
            label: 'Kausal-Adverbien',
            value: this.text.analysis?.data.additional_values.num_causal_connectors || 0,
          },
        ],
        label: 'Kausalität',
      },
      passive: {
        text_propertis: [
          {
            name: 'passive',
            label: 'Passiv-Konstruktionen',
            value: this.text.analysis?.data.additional_values.num_passive_sentences || 0,
          },
        ],
        label: 'Passiv',
      },
      dialog: {
        text_propertis: [
          {
            name: 'dialog_particles',
            label: 'Dialog-Partikel',
            value: this.text.analysis?.data.additional_values.num_dialog_particles || 0,
          },
          {
            name: 'dialog_phrases',
            label: 'Dialog-Formulierungen',
            value: this.text.analysis?.data.additional_values.num_dialog_phrases || 0,
          },
        ],
        label: 'Dialog',
      },
    };
  }
}
